/**
 * The prompt editor.
 *
 * Half of the tuning loop: run the suite, read what failed, change the text,
 * run it again. The `Prompt` table has existed since the first migration with
 * nothing writing to it, so every run so far has used the code default.
 *
 * The screen is built around the invariant it inherits from the store:
 * **saving is not publishing.** A new version is created inactive, and putting
 * it live is a second, separate click. That is deliberately one more step than
 * feels necessary — a system where typing changes what customers are told is a
 * system where a typo is a production incident.
 *
 * Two things are always visible, because both have surprised people:
 *
 *  - What is actually IN FORCE, including when that is the global default
 *    rather than this brand's own text, or the code default because nothing is
 *    active at all.
 *  - That the non-negotiable rules are appended by the code regardless. Nobody
 *    should have to discover by experiment that they cannot delete the medical
 *    boundary by clearing this box.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, ShieldCheck } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { useBrands } from '../api/admin';
import { Badge, Button, Card, CardHeader } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { formatDate } from '../lib/format';

interface InForce {
  id: string;
  key: string;
  version: number;
  body: string;
  scope: 'brand' | 'global';
}

interface PromptSummary {
  key: string;
  description: string;
  inForce: InForce | null;
  versions: Array<{
    id: string;
    version: number;
    active: boolean;
    scope: 'brand' | 'global';
    characters: number;
  }>;
}

interface VersionRow {
  id: string;
  brandId: string | null;
  key: string;
  version: number;
  body: string;
  active: boolean;
  createdAt: string;
  approvedByEvalRunId: string | null;
  scope: 'brand' | 'global';
}

export function PromptsPage() {
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string>('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const scope = brandId || undefined;
  const list = useQuery({
    queryKey: ['prompts', scope ?? 'global'],
    queryFn: () => apiGet<PromptSummary[]>(`/api/prompts${scope ? `?brandId=${scope}` : ''}`),
  });

  const key = selectedKey ?? list.data?.[0]?.key ?? null;

  if (list.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label htmlFor="prompt-scope" className="eyebrow text-ink-500">
          Scope
        </label>
        <select
          id="prompt-scope"
          value={brandId}
          onChange={(event) => setBrandId(event.target.value)}
          className="rounded border border-line bg-surface px-2 py-1 text-sm text-ink-900"
        >
          {/* The global default is a first-class scope, not an absence of one. */}
          <option value="">Global default (all brands)</option>
          {(brands.data ?? []).map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </select>

        <p className="text-xs text-ink-400">
          {brandId
            ? 'A brand prompt beats the global default, whatever the version numbers.'
            : 'Inherited by every brand that has no prompt of its own.'}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Card className="h-fit">
          <CardHeader title="Intents" meta={`${list.data?.length ?? 0}`} />
          <ul>
            {(list.data ?? []).map((prompt) => (
              <li key={prompt.key}>
                <button
                  type="button"
                  onClick={() => setSelectedKey(prompt.key)}
                  aria-current={key === prompt.key}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-sm transition-colors duration-fast last:border-b-0',
                    key === prompt.key
                      ? 'bg-accent-50 font-medium text-accent-700'
                      : 'text-ink-700 hover:bg-surface-sunken',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{prompt.key.replace(/_/g, ' ')}</span>
                  {/* An intent with nothing live falls back to the code
                      default. That is the interesting state, so it is the one
                      that gets a badge. */}
                  {prompt.inForce ? (
                    <Badge tone={prompt.inForce.scope === 'brand' ? 'accent' : 'neutral'}>
                      v{prompt.inForce.version}
                    </Badge>
                  ) : (
                    <span className="text-2xs text-ink-300">code default</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Card>

        {key ? <PromptDetail promptKey={key} brandId={brandId || null} /> : null}
      </div>
    </div>
  );
}

function PromptDetail({ promptKey, brandId }: { promptKey: string; brandId: string | null }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const scopeQuery = brandId ? `?brandId=${brandId}` : '';
  const { data } = useQuery({
    queryKey: ['prompt', promptKey, brandId ?? 'global'],
    queryFn: () =>
      apiGet<{ key: string; inForce: InForce | null; versions: VersionRow[] }>(
        `/api/prompts/${promptKey}${scopeQuery}`,
      ),
  });

  // What is live for THIS scope, which is not the same as what is in force —
  // a brand with nothing of its own inherits the global.
  const ownVersions = useMemo(
    () => (data?.versions ?? []).filter((v) => (brandId ? v.brandId === brandId : v.brandId === null)),
    [data, brandId],
  );
  const live = ownVersions.find((v) => v.active) ?? null;
  const body = draft ?? live?.body ?? '';

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['prompt', promptKey] });
    await queryClient.invalidateQueries({ queryKey: ['prompts'] });
  };

  const publish = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; version: number; warnings: string[] }>('/api/prompts', {
        key: promptKey,
        brandId,
        body,
      }),
    onSuccess: async (result) => {
      setWarnings(result.warnings);
      setProblem(null);
      setDraft(null);
      setNote(
        `Saved as version ${result.version}. It is not live — activate it below when you want it used.`,
      );
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'That did not save.');
      setNote(null);
    },
  });

  const activate = useMutation({
    mutationFn: (id: string) => apiPost(`/api/prompts/${id}/activate`),
    onSuccess: async () => {
      setNote('Live. The next run for this scope will use it.');
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not activate that version.'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => apiPost(`/api/prompts/${id}/deactivate`),
    onSuccess: async () => {
      setNote('Taken down. This scope now falls back to the global default, or to the code default.');
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) =>
      setProblem(error instanceof ApiError ? error.message : 'Could not deactivate that version.'),
  });

  const dirty = draft !== null && draft !== (live?.body ?? '');
  const busy = publish.isPending || activate.isPending || deactivate.isPending;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={promptKey.replace(/_/g, ' ')}
          meta={
            data?.inForce
              ? `In force: ${data.inForce.scope} v${data.inForce.version}`
              : 'In force: the code default'
          }
          action={
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || busy}
              onClick={() => publish.mutate()}
            >
              Save as new version
            </Button>
          }
        />

        <div className="px-4 py-3">
          <textarea
            value={body}
            onChange={(event) => setDraft(event.target.value)}
            rows={16}
            spellCheck
            placeholder="Instructions for this intent. The non-negotiable rules are appended by the code and cannot be removed here."
            className="w-full rounded border border-line bg-surface px-3 py-2 font-mono text-xs leading-relaxed text-ink-900"
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-2xs text-ink-400">
            <span className="tabular">{body.length} characters</span>
            {dirty ? <span className="text-hold-700">Unsaved</span> : null}
          </div>

          {warnings.map((warning) => (
            <p key={warning} className="mt-2 flex items-start gap-1.5 text-xs text-hold-700">
              <AlertTriangle size={13} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
              {warning}
            </p>
          ))}
          {note ? <p className="mt-2 text-xs text-ok-700">{note}</p> : null}
          {problem ? <p className="mt-2 text-xs text-stop-700">{problem}</p> : null}
        </div>

        <div className="flex items-start gap-2 border-t border-line px-4 py-2.5">
          <ShieldCheck size={14} strokeWidth={1.75} className="mt-px shrink-0 text-ink-400" aria-hidden />
          <p className="text-2xs text-ink-400">
            The non-negotiable rules — never diagnose or advise on health, never claim a write that
            was not confirmed, never disclose being an AI, quote only the refund figure policy
            authorised — are appended by the code after this text and cannot be edited or removed
            from here. They are also checked again on the finished reply, outside the prompt, so a
            model that ignores them still cannot send.
          </p>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Versions"
          meta={ownVersions.length === 0 ? 'nothing saved for this scope' : undefined}
        />
        {ownVersions.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <FileText size={24} strokeWidth={1.5} className="mx-auto mb-2 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">No prompt saved here yet.</p>
            <p className="mx-auto mt-1 max-w-[460px] text-sm text-ink-400">
              {brandId
                ? 'This brand inherits the global default. Saving one here overrides it for this brand only.'
                : 'Every brand is using the prompt built into the code. Saving one here replaces it everywhere.'}
            </p>
          </div>
        ) : (
          <ul>
            {ownVersions.map((version) => (
              <li
                key={version.id}
                className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-b-0"
              >
                <span className="tabular w-10 text-sm font-medium text-ink-900">
                  v{version.version}
                </span>
                <span className="text-2xs text-ink-400">{formatDate(version.createdAt)}</span>
                <span className="tabular text-2xs text-ink-400">{version.body.length} chars</span>
                {version.approvedByEvalRunId ? (
                  <Badge tone="fact">eval-approved</Badge>
                ) : null}

                <span className="ml-auto flex items-center gap-2">
                  {version.active ? (
                    <>
                      <Badge tone="accent">live</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => deactivate.mutate(version.id)}
                      >
                        Take down
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => activate.mutate(version.id)}
                    >
                      Make live
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
